import { firestore } from "@/services/firebase";
import {
  CHESS_SIDE,
  CHESS_START_POSITION,
  GAME_RESULT,
} from "vue-chess-shared/constants";
import { hasCommenced } from "vue-chess-shared/utils/match-helper";

export default {
  data() {
    return {
      gameData: {},
      gameSettings: {},
      stopped: true,
      white: null,
      black: null,
      selectedMoveIndex: null,
    };
  },

  // anything return from provide
  // will be make available to all childs via inject
  provide: function () {
    return {
      onPlayerReady: this.handlePlayerReady,
      onAiMakeMove: this.handleAiMove,
      onAiAcceptDraw: this.acceptDraw,
      onAiDenyDraw: this.denyDraw,
      onDataManagerReady: this.handleDataManagerReady,
      onGameDataReady: this.handleGameDataReady,
      onGameDataUpdated: this.handleGameDataUpdated,
      onGameSettingsUpdated: this.handleGameSettingsUpdated,
    };
  },

  watch: {
    isReady: {
      immediate: true,
      handler() {
        if (!this.isReady) {
          return;
        }

        this.nextMove(this.gameData.turnColor, this.pgn, this.initialFen);
      },
    },
  },

  computed: {
    isReady() {
      return (
        !this.stopped && !!this.gameData.match && !!this.white && !!this.black
      );
    },

    matchId() {
      return this.gameData.matchId;
    },

    match() {
      return this.gameData.match || {};
    },

    movesStats() {
      return this.gameData.movesStats || [];
    },

    player1() {
      return this.match.player1 || {};
    },

    player2() {
      return this.match.player2 || {};
    },

    timeData() {
      return this.gameData.timeData;
    },

    pgn() {
      return this.gameData.pgn;
    },

    initialFen() {
      // if initialFen is blank then set it to the starting position of a chess game
      return this.match.initial_fen || CHESS_START_POSITION;
    },

    playerColor() {
      return this.gameData.playerColor;
    },

    movable() {
      return this.match.result ? "" : this.playerColor;
    },

    leewayDuration() {
      return this.gameData.leewayDuration;
    },

    lastMove() {
      return this.movesStats.at(-1);
    },

    blackToStart() {
      return this.gameData.blackToStart;
    },

    canAbort() {
      // can abort if have not made any move yet
      const whiteMoves = this.movesStats.filter(
        (m) => m.color === CHESS_SIDE.WHITE
      );
      const blackMoves = this.movesStats.filter(
        (m) => m.color === CHESS_SIDE.BLACK
      );

      return (
        this.matchId &&
        !this.match.tournamentId &&
        this.leewayDuration &&
        !hasCommenced(this.match, this.movesStats) &&
        !this.match.result &&
        this.playerColor &&
        ((this.playerColor === CHESS_SIDE.WHITE && !whiteMoves.length) ||
          (this.playerColor === CHESS_SIDE.BLACK && !blackMoves.length))
      );
    },

    canResign() {
      return this.matchId && this.playerColor && !this.match.result;
    },

    showThreats() {
      // can only see threats in case of: spectators, player (only when game ended)
      // because it gives unfair advantages to player who can see threats
      return (
        this.gameSettings.showThreats &&
        this.movesStats.length > 0 &&
        (!!this.match.result || !this.playerColor)
      );
    },

    timeOffets() {
      if (!this.timeData) return 0;
      return this.timeData.timeOffets || {};
    },

    firestoreTimeOffset() {
      if (!this.timeData) return 0;
      return this.timeData.firestoreTimeOffset || 0;
    },

    bindFirestore() {
      return this.gameData.bindFirestore;
    },

    gameType() {
      return this.gameData.gameType;
    },

    matchRef() {
      return firestore.collection("matches").doc(this.matchId);
    },
  },

  methods: {
    handlePlayerReady(color, player) {
      this[color] = player;
    },

    handleDataManagerReady(dataManager) {
      this.dataManager = dataManager;
    },

    handleAiMove(color, move) {
      if (this.stopped) {
        console.log("Game stopped, ai move generated ignore");
        return;
      }

      if (this.match.result) {
        console.log(
          color,
          "made an invalid move. Game already ended:",
          this.match.result
        );
        return;
      }

      if (this.$refs.chessboard) {
        this.$refs.chessboard.makeMove(move.from, move.to, move.promotion);
      }
    },

    handleViewMove(data) {
      // don't view move when match is still playing
      if (!this.match.result && this.playerColor) {
        return;
      }

      const { moveIndex, pgn, timeData } = data;

      // temporary view pgn from a specific move
      this.gameData.pgn = pgn ?? this.initialFen;
      this.selectedMoveIndex = moveIndex;

      if (timeData) {
        const isLastMove =
          moveIndex >= 0 && moveIndex === this.movesStats.length - 1;
        // update time data of a specific move
        this.updateTimeData(timeData, isLastMove);
      }
    },

    handleAfterMove(moveData) {
      const { pgn, nextColor, isGameOver } = moveData;

      if (this.dataManager) this.dataManager.addMove(moveData);

      if (!isGameOver) {
        this.nextMove(nextColor, pgn, this.initialFen);
      }
    },

    handleGameDataReady(data) {
      this.gameData = data;
    },

    handleGameDataUpdated(data) {
      this.gameData = data;
      this.selectedMoveIndex = this.movesStats.length - 1;
      this.updateTimeData(this.timeData);
    },

    handleGameSettingsUpdated(gameSettings) {
      this.gameSettings = gameSettings;
    },

    nextMove(turnColor, pgn, initialFen) {
      if (this.stopped) {
        console.log("Game stopped, no next move");
        return;
      }

      // continue current game by making the next move if game isn't over
      this[turnColor].nextMove(pgn, initialFen);
    },

    updateTimeData(timeData, isLastMove = true) {
      this.gameData.timeData = timeData;

      // when match is timed out, no move is made,
      // so timeLeft from data is actually the time left from the latest white/black move
      // in this case, set time left to 0 to reflect correct match status
      if (isLastMove && this.match.result === GAME_RESULT.TIMEOUT) {
        if (this.match.player_1_score > this.match.player_2_score) {
          // black timed out
          this.gameData.timeData.black.timeLeft = 0;
        } else if (this.match.player_1_score < this.match.player_2_score) {
          // white timed out
          this.gameData.timeData.white.timeLeft = 0;
        }
      }
    },

    reset() {
      this.stopped = false;
      this.white = null;
      this.black = null;
      this.selectedMoveIndex = null;
    },

    abort() {
      this.stopped = true;
      if (this.dataManager) this.dataManager.abort();
    },

    resign() {
      this.stopped = true;
      if (this.dataManager) this.dataManager.resign();
    },

    requestDraw() {
      if (this.dataManager) this.dataManager.requestDraw();
    },

    acceptDraw() {
      if (this.dataManager) this.dataManager.acceptDraw();
    },

    denyDraw() {
      if (this.dataManager) this.dataManager.denyDraw();
    },
  },

  mounted() {
    this.mediator = this;
  },
};
